import {
  ChangeDetectionStrategy,
  Component,
  OnChanges,
  SimpleChanges,
  computed,
  inject,
  input,
  signal,
} from '@angular/core';
import { CommonModule, DatePipe, DecimalPipe } from '@angular/common';
import { TranslateModule }        from '@ngx-translate/core';
import { FormsModule }            from '@angular/forms';
import { catchError, forkJoin, of } from 'rxjs';
import { Router }                 from '@angular/router';

import { PanelComponent }            from '@libs/front/components/panel/panel.component';
import { ToggleComponent }           from '@libs/front/components/toggle/toggle.component';
import { ButtonComponent }           from '@libs/front/components/button/button.component';
import { PopupComponent }            from '@libs/front/components/popup/popup.component';
import { ScrollableComponent }       from 'src/app/shared/components/scrollable/scrollable.component';
import { InputComponent }            from '@libs/front/components/input/input.component';
import { SelectComponent, SelectOption } from '@libs/front/components/select/select.component';
import { DatePickerComponent }           from '@libs/front/components/date-picker/date-picker.component';
import { HttpService }               from 'src/app/services/http.service';
import { UtilisateurService }        from 'src/app/services/utilisateur.service';
import { DeviceService }             from 'src/app/services/device.service';
import { FonctionsService }          from 'src/app/services/fonctions-partagees.service';
import { BlastService }              from 'src/app/services/blast.service';
import { KpiService }                from 'src/app/services/kpi.service';
import { DonneeAnalyse }             from 'src/app/bo/donneeAnalyse';
import { ConfigurationSaisieDonneesAnalyse } from 'src/app/bo/configurationSaisieDonneesAnalyse';
import { Tir }                       from 'src/app/bo/tir';
import { Zone }                      from 'src/app/bo/zone';
import { TypeTir }                   from 'src/app/bo/TypeTir';
import { Projet }                    from 'src/app/bo/projet';
import { OperationType }             from 'src/app/bo/operationTypeEnum';

interface KpiTile {
  code:  string;
  label: string;
  value: number | null;
  unit:  string;
}

/** Tuiles du haut du dashboard : référentiel KPI (KPI.<code>) → libellé et unité par défaut. */
const KPI_TILES: Omit<KpiTile, 'value'>[] = [
  { code: 'NOMBRE_TROU',        label: 'BLAST_DASHBOARD.HOLES',                unit: ''   },
  { code: 'TOTAL_DRILL_LENGTH', label: 'BLAST_DASHBOARD.TOTAL_DRILLED_LENGTH', unit: 'm'  },
  { code: 'QTY_EXPLOSIF',       label: 'BLAST_DASHBOARD.TOTAL_EXPLOSIVES',     unit: 'kg' },
  { code: 'BLAST_VOLUME',       label: 'BLAST_DASHBOARD.BLAST_VOLUME',         unit: 'm3' },
];

@Component({
  selector:    'app-blast-dashboard',
  standalone:  true,
  imports:     [CommonModule, DatePipe, DecimalPipe, FormsModule, TranslateModule, PanelComponent, ToggleComponent, ButtonComponent, PopupComponent, ScrollableComponent, InputComponent, SelectComponent, DatePickerComponent],
  templateUrl: './blast-dashboard.component.html',
  styleUrls:   ['./blast-dashboard.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BlastDashboardComponent implements OnChanges {
  blastId = input.required<number>();

  readonly userService          = inject(UtilisateurService);
  readonly device               = inject(DeviceService);
  private readonly http         = inject(HttpService);
  private readonly fonctionsService = inject(FonctionsService);
  private readonly blastService = inject(BlastService);
  private readonly kpiService   = inject(KpiService);
  private readonly router       = inject(Router);

  // ── Tir courant ──────────────────────────────────────────────────────────────
  tir              = signal<Tir | null>(null);
  isBlasted        = signal(false);

  // ── KPI ──────────────────────────────────────────────────────────────────────
  private readonly analysisData = signal<DonneeAnalyse[]>([]);
  private readonly kpiConfigs   = signal<ConfigurationSaisieDonneesAnalyse[]>([]);

  readonly kpiTiles = computed<KpiTile[]>(() => {
    const data    = this.analysisData();
    const configs = this.kpiConfigs();
    return KPI_TILES.map(tile => {
      const donnee = data.find(d => d.referentielDonneeAnalyse?.nom === 'KPI.' + tile.code);
      const cfg    = donnee && configs.find(c => c.referentielDonneeAnalyseId === donnee.referentielDonneeAnalyseId);
      return {
        ...tile,
        value: donnee ? this.kpiValue(donnee) : null,
        // L'API convertit les valeurs dans l'unité d'affichage du site : même résolution que l'onglet KPI.
        unit:  tile.unit && (
          extractSymbol(cfg?.displayUnit)
          || donnee?.referentielDonneeAnalyse.nomUniteReferentiel
          || tile.unit
        ),
      };
    });
  });

  // ── Popup : Tiré ─────────────────────────────────────────────────────────────
  showBlastedPopup = signal(false);
  blastDate        = signal('');
  blastTime        = signal('');

  // ── Popup : Supprimer ─────────────────────────────────────────────────────────
  showDeletePopup  = signal(false);

  // ── Popup : Modifier ─────────────────────────────────────────────────────────
  readonly operationalCategories = [
    { id: 'blast_ug', nom: 'Blast UG' },
    { id: 'blast_op', nom: 'Blast OP' },
  ] as const;

  showModifyPopup          = signal(false);
  modifyOpCategory         = signal<string | null>(null);
  modifyOpCategoryDisabled = signal(false);
  modifyNom                = signal('');
  modifyNiveau     = signal('');
  modifyDatePrev   = signal('');
  modifyDateEff    = signal('');
  modifyHeureEff   = signal(0);
  modifyProjetId   = signal<number | null>(null);
  modifyZoneId     = signal<number | null>(null);
  modifyTypeTirId  = signal<number | null>(null);
  projets          = signal<Projet[]>([]);
  zones            = signal<Zone[]>([]);
  blastTypes       = signal<TypeTir[]>([]);

  // Options pour app-select
  readonly opCategoryOptions: SelectOption[] = [
    { id: 'blast_ug', label: 'Blast UG' },
    { id: 'blast_op', label: 'Blast OP' },
  ];
  readonly heureOptions: SelectOption[] =
    [0,1,2,3,4,5,6,7,8,9,10,11,12,13,14,15,16,17,18,19,20,21,22,23,24]
      .map(h => ({ id: h, label: `${h}h` }));
  projetOptions  = computed<SelectOption[]>(() => this.projets().map(p  => ({ id: p.projetId,   label: p.nom })));
  zoneOptions    = computed<SelectOption[]>(() => this.zones().map(z    => ({ id: z.zoneId,     label: z.nom })));
  typeOptions    = computed<SelectOption[]>(() => this.blastTypes().map(t => ({ id: t.typeTirId, label: t.nom })));

  modifyFormValid = computed(() =>
    !!this.modifyNom().trim() &&
    !!this.modifyDatePrev() &&
    this.modifyZoneId() !== null &&
    this.modifyTypeTirId() !== null
  );

  // ─────────────────────────────────────────────────────────────────────────────

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['blastId']) {
      const blastId = this.blastId();
      this.tir.set(null);
      this.analysisData.set([]);
      this.kpiConfigs.set([]);
      this.http.get<Tir>('Blasts/' + blastId).subscribe(tir => {
        this.tir.set(tir);
        this.isBlasted.set(tir.blasted === 1);
      });
      // Endpoint brut : KpiService.getBlastAnalysisData remplace referentielDonneeAnalyse.nom par sa traduction,
      // or les tuiles sont retrouvées par leur clé (KPI.<code>).
      const siteId = this.userService.currentSite()?.siteId;
      forkJoin([
        this.http.get<DonneeAnalyse[]>(`Blasts/${blastId}/AnalysisData`),
        siteId
          ? this.kpiService.getSiteConfigAnalysisEntry(siteId).pipe(catchError(() => of([])))
          : of([]),
      ]).subscribe({
        next: ([data, configs]) => {
          if (this.blastId() !== blastId) return;
          this.analysisData.set(data);
          this.kpiConfigs.set(configs);
        },
        error: () => {
          if (this.blastId() !== blastId) return;
          this.analysisData.set([]);
          this.kpiConfigs.set([]);
        },
      });
    }
  }

  /** Valeur retenue d'un KPI, même priorité que l'onglet KPI : manuelle > mesurée > design. */
  private kpiValue(donnee: DonneeAnalyse): number | null {
    const raw = donnee.manualValue || donnee.measuredValue || donnee.designValue || donnee.valeur;
    if (raw == null || raw === '') return null;
    const value = Number(String(raw).replace(',', '.'));
    return Number.isFinite(value) ? value : null;
  }

  // ── Tiré ─────────────────────────────────────────────────────────────────────

  onBlastedToggle(checked: boolean): void {
    if (!checked || !this.tir() || this.tir()!.blasted === 1) return;
    const now = new Date();
    this.blastDate.set(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`);
    this.blastTime.set(now.toTimeString().split(' ')[0].substring(0, 5));
    this.isBlasted.set(true);
    this.showBlastedPopup.set(true);
  }

  cancelBlastedPopup(): void {
    this.showBlastedPopup.set(false);
    this.isBlasted.set(false);
  }

  confirmBlastedStatus(): void {
    const tir = this.tir();
    if (!tir || !this.blastDate() || !this.blastTime()) return;
    const [year, month, day] = this.blastDate().split('-').map(Number);
    const date = new Date(year, month - 1, day);
    const [hours, minutes] = this.blastTime().split(':').map(Number);
    date.setHours(hours);
    date.setMinutes(minutes);
    this.blastService.markAsBlasted(tir.tirId, date, true).subscribe({
      next: (updated) => {
        tir.blasted = 1;
        tir.dateTirEffective = updated.dateTirEffective;
        this.tir.set(tir);
        this.isBlasted.set(true);
        this.showBlastedPopup.set(false);
      },
      error: () => {
        this.isBlasted.set(false);
        this.showBlastedPopup.set(false);
      },
    });
  }

  // ── Supprimer ─────────────────────────────────────────────────────────────────

  supprimerBlast(): void {
    const tir = this.tir();
    if (!tir) return;
    this.fonctionsService.loading_screen(true);
    this.http.delete('Blasts/' + tir.tirId).subscribe({
      next: (result) => {
        this.fonctionsService.loading_screen(false);
        this.showDeletePopup.set(false);
        if (result) {
          this.fonctionsService.launch_toast(true, true);
          this.router.navigate(['mysite', this.userService.currentSite()?.siteId]);
        }
      },
      error: () => this.fonctionsService.loading_screen(false),
    });
  }

  // ── Modifier ──────────────────────────────────────────────────────────────────

  ouvrirModaleModif(): void {
    const tir = this.tir();
    if (!tir) return;
    this.fonctionsService.loading_screen(true);
    const siteId = this.userService.currentSite()?.siteId;
    forkJoin([
      this.http.get<Projet[]>('Sites/' + siteId + '/Projects'),
      this.http.get<Zone[]>('Sites/' + siteId + '/Zones'),
      this.http.get<TypeTir[]>('Sites/' + siteId + '/BlastTypes'),
    ]).subscribe({
      next: ([projets, zones, blastTypes]) => {
        this.fonctionsService.loading_screen(false);
        this.projets.set(projets);
        this.zones.set(zones);
        this.blastTypes.set(blastTypes);
        // Operational category depuis operationType du site
        const opType = this.userService.currentSite()?.operationType;
        if (opType === OperationType.OP) {
          this.modifyOpCategory.set('blast_op');
          this.modifyOpCategoryDisabled.set(true);
        } else if (opType === OperationType.UG) {
          this.modifyOpCategory.set('blast_ug');
          this.modifyOpCategoryDisabled.set(true);
        } else {
          this.modifyOpCategoryDisabled.set(false);
        }
        this.modifyNom.set(tir.nom ?? '');
        this.modifyProjetId.set(tir.projetId ?? null);
        this.modifyDatePrev.set(
          tir.dateTirPrevisionnelle
            ? new Date(tir.dateTirPrevisionnelle).toISOString().split('T')[0]
            : ''
        );
        this.modifyNiveau.set(tir.niveau ?? '');
        this.modifyZoneId.set(tir.zoneId ?? null);
        this.modifyTypeTirId.set(tir.typeTirId ?? null);
        const dateEff = tir.dateTirEffective ? new Date(tir.dateTirEffective) : null;
        this.modifyDateEff.set(dateEff ? dateEff.toISOString().split('T')[0] : '');
        this.modifyHeureEff.set(dateEff ? dateEff.getHours() : 0);
        this.showModifyPopup.set(true);
      },
      error: () => this.fonctionsService.loading_screen(false),
    });
  }

  submitModification(): void {
    const tir = this.tir();
    if (!tir || !this.modifyFormValid()) return;
    const zone     = this.zones().find(z => z.zoneId === this.modifyZoneId())!;
    const typeTir  = this.blastTypes().find(t => t.typeTirId === this.modifyTypeTirId())!;
    tir.nom                   = this.modifyNom().trim();
    tir.niveau                = this.modifyNiveau();
    tir.projetId              = this.modifyProjetId() ?? tir.projetId;
    const [prevYear, prevMonth, prevDay] = this.modifyDatePrev().split('-').map(Number);
    tir.dateTirPrevisionnelle = new Date(prevYear, prevMonth - 1, prevDay);
    tir.zoneId                = this.modifyZoneId()!;
    tir.zone                  = zone;
    tir.nomZone               = zone?.nom;
    tir.typeTirId             = this.modifyTypeTirId()!;
    tir.typeTir               = typeTir;
    tir.nomTypeTir            = typeTir?.nom;
    if (this.modifyDateEff()) {
      const [effYear, effMonth, effDay] = this.modifyDateEff().split('-').map(Number);
      const dateEff = new Date(effYear, effMonth - 1, effDay);
      dateEff.setHours(this.modifyHeureEff(), 0, 0, 0);
      tir.dateTirEffective = dateEff;
    }
    this.fonctionsService.loading_screen(true);
    this.http.put<Tir, Tir>('Blasts', tir).subscribe({
      next: (result) => {
        this.fonctionsService.loading_screen(false);
        this.tir.set(result);
        this.showModifyPopup.set(false);
        this.fonctionsService.launch_toast(true, false);
      },
      error: () => this.fonctionsService.loading_screen(false),
    });
  }
}

/** Symbole d'une unité d'affichage configurée (« Mètre - m » → « m »). */
function extractSymbol(displayUnit: string | null | undefined): string {
  if (!displayUnit) return '';
  const parts = displayUnit.split(' - ');
  return parts.length > 1 ? parts[1].trim() : displayUnit;
}
